import type { MatchBucket } from "@repo/app-types/entities/matchBucket";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { ChartCard } from "@repo/ui/Cards/chartCard";
import { DateTime } from "luxon";
import { type FC, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { TrendChart } from "../../teammatePerformance/trendChart.js";
import { computePercent } from "../signalStatsFormatters.js";

type ChartMode = "percentage" | "count";

const TOOLTIP_DATE_FORMAT = "MMM d, yyyy";
const AXIS_DATE_FORMAT = "MMM d";
const DAY_MS = 24 * 60 * 60 * 1000;

export const SignalMatchRateChart: FC<{
  buckets: readonly MatchBucket[];
  startDate: DateTime;
  endDate: DateTime;
  /** Whether the selected range is anchored to "now". Controls whether the
   * hover tooltip shows relative ("12d ago") or absolute ("Apr 7, 2026") dates. */
  isRelativeRange: boolean;
}> = ({ buckets, startDate, endDate, isRelativeRange }) => {
  const [mode, setMode] = useState<ChartMode>("percentage");

  const filteredBuckets = useMemo(() => {
    const startMs = startDate.toMillis();
    const endMs = endDate.toMillis();
    // Boundary-inclusive: keep any bucket whose day overlaps [startMs, endMs),
    // so a partial first/last bucket (e.g. a day straddling dateAfter) still
    // renders. The server already filters underlying rows by the exact range,
    // so partial buckets report only the in-range rows.
    return buckets.filter((b) => {
      const bucketStartMs = new Date(b.bucketStart).getTime();
      return bucketStartMs + DAY_MS > startMs && bucketStartMs < endMs;
    });
  }, [buckets, startDate, endDate]);

  const lineData = useMemo(
    () =>
      filteredBuckets.map((b) =>
        mode === "percentage"
          ? computePercent(b.matchCount, b.interactionCount)
          : b.matchCount,
      ),
    [filteredBuckets, mode],
  );

  const xAxisLabels = useMemo(() => {
    const first = filteredBuckets.at(0);
    const last = filteredBuckets.at(-1);
    if (!first || !last) {
      return undefined;
    }
    return {
      start: DateTime.fromISO(first.bucketStart).toFormat(AXIS_DATE_FORMAT),
      end: DateTime.fromISO(last.bucketStart).toFormat(AXIS_DATE_FORMAT),
    };
  }, [filteredBuckets]);

  const getTooltipDateLabel = useMemo(
    () => (index: number) => {
      const bucket = filteredBuckets[index];
      return bucket
        ? DateTime.fromISO(bucket.bucketStart).toFormat(TOOLTIP_DATE_FORMAT)
        : "";
    },
    [filteredBuckets],
  );

  return (
    <ChartCard
      title={
        mode === "percentage" ? "Match rate over time" : "Match count over time"
      }
      actions={
        <>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => setMode("percentage")}
            className={twMerge(
              "font-normal transition-opacity",
              mode !== "percentage" && "opacity-50",
            )}
          >
            % of calls
          </Button>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => setMode("count")}
            className={twMerge(
              "font-normal transition-opacity",
              mode !== "count" && "opacity-50",
            )}
          >
            Count
          </Button>
        </>
      }
    >
      <div className="h-[200px]">
        <TrendChart
          className="h-full w-full"
          lines={[{ key: "matchRate", data: lineData }]}
          minYRange={1}
          renderValue={(value) =>
            mode === "percentage" ? `${Math.round(value)}%` : String(value)
          }
          getColorClassName={() => "text-gray-600"}
          showsXAxisLabels
          showsYAxisLabels
          timeUnit="day"
          xAxisLabels={isRelativeRange ? undefined : xAxisLabels}
          getTooltipDateLabel={
            isRelativeRange ? undefined : getTooltipDateLabel
          }
        />
      </div>
    </ChartCard>
  );
};
